/**
 * ColumnConfigStep - Step 4 of view creation wizard
 *
 * Column selection for individual columns from base dataset AND any joined datasets.
 *
 * Step Order:
 * 1. Basic Information
 * 2. File Selection Rules
 * 3. Advanced Transformations
 * 4. Column Configuration (this step)
 * 5. Refresh Settings
 * 6. Review & Create
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Search, Loader2, Edit, Plus, Database, Link2, AlertTriangle, Layers } from 'lucide-react';
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/ui/data-table';
import { ColumnTransformationBuilder } from './transformations/ColumnTransformationBuilder';
import { BatchJsonDecompose } from './transformations/BatchJsonDecompose';
import { usePreviewColumns } from '@/hooks/useViews';
import type { ViewFormData } from '@/pages/ViewCreatePage';
import type { ViewColumn } from '@/types/views';
import { ErrorBoundary } from '@/components/ui/error-boundary';

/** Generate a UUID, with fallback for non-secure contexts (HTTP). */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0; // nosemgrep: rules.lgpl.nodejs_scan.javascript-crypto-rule-node_insecure_random_generator  -- non-secure-context UUID fallback for an opaque element id, not a security token
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * UI Configuration Constants
 * Centralized configuration for consistent UI sizing and behavior
 */
const UI_CONSTANTS = {
  /** Min height of the column table */
  GRID_HEIGHT: '500px',
  /** Debounce delay for alias input changes (in milliseconds) */
  ALIAS_DEBOUNCE_DELAY: 300,
  /** Maximum length for sanitized error messages */
  ERROR_MESSAGE_MAX_LENGTH: 500,
  /** Cache time for query data freshness (in milliseconds) */
  QUERY_STALE_TIME: {
    SHORT: 60000,    // 1 minute
    MEDIUM: 300000,  // 5 minutes
  },
  /** API timeout durations (in milliseconds) */
  TIMEOUTS: {
    PREVIEW_COLUMNS: 30000, // 30 seconds
  },
} as const;

/**
 * Sanitize error messages for display
 * Limits length to prevent UI issues.
 * Note: XSS protection is handled by React's automatic text escaping
 * when rendering as text content (not using dangerouslySetInnerHTML).
 */
function sanitizeErrorMessage(message: string): string {
  if (!message) return 'An unknown error occurred';

  // Limit length to prevent UI issues
  if (message.length > UI_CONSTANTS.ERROR_MESSAGE_MAX_LENGTH) {
    return message.substring(0, UI_CONSTANTS.ERROR_MESSAGE_MAX_LENGTH) + '...';
  }

  return message;
}

interface ColumnConfigStepProps {
  formData: ViewFormData;
  updateFormData: (updates: Partial<ViewFormData>) => void;
}

/**
 * Runtime prop validation for ColumnConfigStep
 * Validates critical props to catch issues early in development
 */
function validateProps(props: ColumnConfigStepProps): void {
  if (!props.formData) {
    throw new Error('ColumnConfigStep: formData prop is required');
  }

  if (typeof props.updateFormData !== 'function') {
    throw new Error('ColumnConfigStep: updateFormData must be a function');
  }

  // Validate formData structure
  if (!Array.isArray(props.formData.rules)) {
    console.warn('ColumnConfigStep: formData.rules should be an array, received:', typeof props.formData.rules);
  }

  if (!Array.isArray(props.formData.columns)) {
    console.warn('ColumnConfigStep: formData.columns should be an array, received:', typeof props.formData.columns);
  }

  if (props.formData.joins !== undefined && !Array.isArray(props.formData.joins)) {
    console.warn('ColumnConfigStep: formData.joins should be an array when defined, received:', typeof props.formData.joins);
  }
}

/**
 * Row data type for the DataTable.
 */
interface ColumnRowData {
  rowId: string; // Unique row identifier: "columnName" for non-conflicts, "columnName::source" for conflicts, ViewColumn id for decomposed
  columnName: string;
  source: string;
  sourceType: 'base' | 'join' | 'decomposed';
  hasConflict: boolean; // True if column name appears in multiple sources
  conflictSources?: string[]; // List of sources with this column name
  isDecomposed?: boolean; // True if this row represents a JSON-decomposed derived column
  decomposedFromColumn?: string; // The source column this was decomposed from
  // ---- Committed display state, derived from formData.columns (single source of
  // truth) by the `displayRows` memo. Cell renderers read these from the `data`
  // prop rather than reading a ref during render. ----
  /** Committed column_alias for this row (empty string when none). */
  alias?: string;
  /** True when this is a conflict column with 2+ source versions currently selected. */
  aliasRequired?: boolean;
  /** transform_expression of the (single) matching column entry, if any. */
  transformExpression?: string;
  /** Number of matching column entries that carry a transform_expression (batch JSON decomposition when > 1). */
  transformFieldCount?: number;
}

/**
 * Metadata for a decomposed column row.
 * Tracked in state so that rowData can include decomposed entries
 * without depending on formData.columns directly.
 */
interface DecomposedRowEntry {
  /** ViewColumn id -- used as the table rowId */
  id: string;
  /** The original JSON column that was decomposed */
  sourceColumn: string;
  /** The alias for this extracted field */
  alias: string;
  /** source_name from the ViewColumn (for conflict-qualified sources) */
  sourceName?: string;
  /** Full ViewColumn preserved so transform_expression survives deselection/re-selection */
  viewColumn?: ViewColumn;
}

/**
 * Derive the initial set of decomposed-column rows from a column list.
 *
 * Handles the edit-view case where the form is pre-populated with decomposed
 * entries from the server. Decomposed entries are ViewColumns that carry a
 * transform_expression containing a JSON extraction (`->>'...'` or
 * `json_extract_string(`) AND share a source_column with at least one other such
 * entry. Used as the lazy initializer for `decomposedRows` state (runs once, at
 * mount) so we never call setState synchronously inside an effect.
 */
function computeInitialDecomposedRows(columns: ViewColumn[]): DecomposedRowEntry[] {
  const jsonExtractionPattern = /->>'[^']+'|json_extract_string\(/;
  const grouped = new Map<string, ViewColumn[]>();
  for (const col of columns) {
    if (col.transform_expression && jsonExtractionPattern.test(col.transform_expression)) {
      const key = col.source_column;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(col);
    }
  }
  // Only treat as decomposed if there are 2+ entries for the same source_column
  const entries: DecomposedRowEntry[] = [];
  for (const [sourceColumn, cols] of grouped) {
    if (cols.length >= 2) {
      for (const col of cols) {
        entries.push({
          id: col.id,
          sourceColumn,
          alias: col.column_alias || '',
          sourceName: col.source_name,
          viewColumn: col,
        });
      }
    }
  }
  return entries;
}

/**
 * Source Badge Cell Renderer with conflict warning
 */
const SourceBadgeRenderer: React.FC<{ data: ColumnRowData }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="flex items-center gap-2">
      {data.isDecomposed ? (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Layers className="h-3 w-3" />
          JSON: {data.decomposedFromColumn}
        </Badge>
      ) : data.sourceType === 'base' ? (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Database className="h-3 w-3" />
          Base Dataset
        </Badge>
      ) : (
        <Badge variant="outline" className="flex items-center gap-1">
          <Link2 className="h-3 w-3" />
          {data.source}
        </Badge>
      )}
      {data.hasConflict && (
        <Badge variant="destructive" className="flex items-center gap-1" title={`Column exists in: ${data.conflictSources?.join(', ')}`}>
          <AlertTriangle className="h-3 w-3" />
          Conflict
        </Badge>
      )}
    </div>
  );
};

/**
 * Column Name Cell Renderer with conflict indicator
 */
const ColumnNameRenderer: React.FC<{ data: ColumnRowData }> = ({ data }) => {
  if (!data) return null;

  return (
    <div className="flex items-center gap-2 font-mono text-sm">
      {data.isDecomposed && (
        <span className="text-muted-foreground" title={`Extracted from ${data.decomposedFromColumn}`}>
          <Layers className="h-3 w-3" />
        </span>
      )}
      <span>{data.columnName}</span>
      {data.hasConflict && (
        <span title="Column name conflict - alias recommended">
          <AlertTriangle className="h-3 w-3 text-destructive" />
        </span>
      )}
    </div>
  );
};

/**
 * Alias Cell Renderer (Editable Input) with conflict warning.
 *
 * Display strategy:
 * - `localAlias` (component state) drives the input for responsive keystrokes.
 * - The committed alias is read from `data.alias` (built by the parent's `displayRows`
 *   memo from `formData.columns`, the single source of truth) -- never from a ref
 *   during render. `localAlias` is initialized from `data.alias` on mount, refreshed
 *   from `data.alias` on focus (to catch external changes), and preserved on blur.
 *
 * Eager/immediate propagation is handled by the parent callbacks:
 * - `onAliasChangeRef` eagerly writes the in-progress alias into the working column
 *   list so selection/other handlers see it before the debounce fires.
 * - `onAliasChange` (debounced) propagates the value to formData.
 * - `onAliasChangeImmediate` flushes on blur.
 */
interface AliasCellProps {
  data: ColumnRowData;
  onAliasChange: ((rowId: string, alias: string) => void) & { cancel?: () => void };
  onAliasChangeImmediate: (rowId: string, alias: string) => void;
  onAliasChangeRef: (rowId: string, alias: string) => void;
}

const AliasCellRenderer: React.FC<AliasCellProps> = ({
  data,
  onAliasChange,
  onAliasChangeImmediate,
  onAliasChangeRef,
}) => {
  // Committed alias for this row, from formData.columns via the parent memo.
  const committedAlias = data?.alias || '';

  // Local state for responsive typing. Hoisted to the top of the component so it
  // is called unconditionally (Rules of Hooks) -- no try/catch, no conditional.
  const [localAlias, setLocalAlias] = useState(committedAlias);

  if (!data || !onAliasChange || !onAliasChangeImmediate) return null;

  const displayValue = localAlias;

  // Conflict cells require an alias once 2+ source versions are selected; the
  // selection state is computed by the parent (`data.aliasRequired`).
  const needsAlias = !!data.aliasRequired && !displayValue;

  return (
    <Input
      value={displayValue}
      onChange={(e) => {
        const newValue = e.target.value;
        setLocalAlias(newValue);
        // Eagerly update the working column list so handleRowSelectionChange sees
        // the latest alias even before the debounced formData update fires.
        onAliasChangeRef?.(data.rowId, newValue);
        onAliasChange(data.rowId, newValue);
      }}
      onFocus={() => {
        // Refresh from the committed value at focus time to catch external changes,
        // but ONLY when the committed value is non-empty AND differs from what's
        // already displayed. This prevents wiping the alias the user is editing when
        // the committed source lacks the column_alias field.
        if (committedAlias && committedAlias !== localAlias) {
          setLocalAlias(committedAlias);
        }
      }}
      onBlur={() => {
        // Cancel the pending debounced update -- onBlur flushes immediately, so
        // letting it also fire would double-process the same edit.
        onAliasChange.cancel?.();
        // Flush the current value immediately so formData is up-to-date.
        onAliasChangeImmediate(data.rowId, localAlias);
      }}
      placeholder={needsAlias ? "Alias required - both sources selected" : "Custom name"}
      className={`h-8 text-sm ${needsAlias ? 'border-destructive' : ''}`}
    />
  );
};

/**
 * Transformation Cell Renderer.
 * Reads transformation state from the `data` row prop (built by the parent's
 * `displayRows` memo from formData.columns) -- never from a ref during render.
 */
interface TransformationCellProps {
  data: ColumnRowData;
  onEdit: (rowId: string) => void;
  onBatchEdit: (rowId: string) => void;
}

const TransformationCellRenderer: React.FC<TransformationCellProps> = ({ data, onEdit, onBatchEdit }) => {
  if (!data || !onEdit) return null;

  // Batch JSON decomposition = more than one matching entry carries a transform.
  const isBatchDecomposed = (data.transformFieldCount ?? 0) > 1;

  if (isBatchDecomposed) {
    return (
      <div className="flex items-center justify-between gap-2">
        <Badge variant="secondary" className="text-xs gap-1">
          <Layers className="h-3 w-3" />
          JSON: {data.transformFieldCount} fields
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onBatchEdit(data.rowId)}
          className="gap-1"
        >
          <Edit className="h-3 w-3" />
          Edit
        </Button>
      </div>
    );
  }

  const transformation = data.transformExpression;
  const hasTransformation = !!transformation;

  return (
    <div className="flex items-center justify-between gap-2">
      {hasTransformation && (
        <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
          {transformation}
        </code>
      )}
      <Button
        variant={hasTransformation ? "ghost" : "outline"}
        size="sm"
        onClick={() => onEdit(data.rowId)}
        className="gap-1"
      >
        {hasTransformation ? (
          <>
            <Edit className="h-3 w-3" />
            Edit
          </>
        ) : (
          <>
            <Plus className="h-3 w-3" />
            Add
          </>
        )}
      </Button>
    </div>
  );
};

export const ColumnConfigStep: React.FC<ColumnConfigStepProps> = ({ formData, updateFormData }) => {
  // Runtime prop validation (development safety check)
  if (import.meta.env.DEV) {
    validateProps({ formData, updateFormData });
  }

  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [sourceInfo, setSourceInfo] = useState<{ base_columns: string[]; joined_columns?: Record<string, string[]> } | null>(null);
  const [columnLoadError, setColumnLoadError] = useState<string | null>(null);
  const [transformationDialogOpen, setTransformationDialogOpen] = useState(false);
  const [editingColumnName, setEditingColumnName] = useState<string | null>(null);
  const [batchDecomposeOpen, setBatchDecomposeOpen] = useState(false);
  const [batchDecomposeColumn, setBatchDecomposeColumn] = useState<string | null>(null);
  const [quickFilterText, setQuickFilterText] = useState<string>('');
  // Initialized once from formData.columns (edit-view pre-population). Using a lazy
  // initializer -- instead of a mount effect that calls setState -- keeps the
  // derivation out of the effect phase (no synchronous setState in an effect).
  const [decomposedRows, setDecomposedRows] = useState<DecomposedRowEntry[]>(
    () => computeInitialDecomposedRows(formData.columns)
  );

  const previewColumns = usePreviewColumns();
  /**
   * TanStack row-selection state, keyed by rowId. Synced both ways with
   * formData.columns: external changes flow in via the selection-sync
   * effect; user checkbox toggles flow out via handleRowSelectionChange.
   */
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Set of joined source names from preview columns response.
  // Used to distinguish joined-source columns from base-source columns when
  // building selection keys (view-on-view base columns store a source_name
  // but should be treated as unqualified base rows in the grid).
  const joinedColumnKeys = useMemo(() => {
    return new Set(sourceInfo?.joined_columns ? Object.keys(sourceInfo.joined_columns) : []);
  }, [sourceInfo]);

  // Ref to track latest column state for debounced/async updates
  const columnsRef = useRef<ViewColumn[]>(formData.columns);

  // Stable ref for updateFormData to prevent cascading callback identity changes.
  // The parent's updateFormData is not wrapped in useCallback, so it gets a new
  // identity on every parent render. Using a ref here breaks the chain that would
  // otherwise cause handleAliasChangeImmediate -> handleAliasChange -> columns
  // to all get new identities, which would make cell renderers remount and
  // destroy the user's in-progress typing.
  const updateFormDataRef = useRef(updateFormData);
  // Keep the ref pointing at the latest updateFormData. Done in an effect (not
  // during render) so we never write a ref during the render phase. This effect
  // is declared before any effect that calls stableUpdateFormData, so the ref is
  // current by the time those run.
  useEffect(() => {
    updateFormDataRef.current = updateFormData;
  });

  // Guard ref to prevent feedback loop between selection sync effect and handleSelectionChanged
  const isSyncingSelection = useRef(false);

  // Guard ref to ensure auto-select-all only fires once on initial load
  const hasAutoSelected = useRef(false);

  // Guard ref: true when the initial selection sync has run at least once.
  // Until this is true, handleRowSelectionChange bails out to prevent the
  // table's initial mount (where no rows are selected) from wiping formData.columns.
  const hasInitialSelectionSynced = useRef(false);

  // Guard counter: incremented when WE initiate a formData.columns update (or an
  // eager ref mutation), so the useEffect that syncs columnsRef from formData can
  // skip (the ref is already up-to-date). A counter (not a boolean) handles the
  // case where multiple eager updates occur before a single useEffect fires --
  // each parent re-render consumes one count, and we only sync from formData once
  // all pending eager updates have been accounted for.
  const selfInitiatedColumnsUpdate = useRef(0);

  const stableUpdateFormData = useCallback((updates: Partial<ViewFormData>) => {
    // When we update columns ourselves, increment the counter so the useEffect
    // that syncs columnsRef from formData will skip (ref already up-to-date).
    if ('columns' in updates) {
      selfInitiatedColumnsUpdate.current++;
    }
    updateFormDataRef.current(updates);
  }, []);

  // Keep ref in sync with formData.columns -- but ONLY for external changes.
  // When we update formData.columns ourselves (alias, selection, transformation),
  // the ref was already eagerly updated before calling stableUpdateFormData.
  // Overwriting the ref here would race with those eager updates and revert data.
  useEffect(() => {
    if (selfInitiatedColumnsUpdate.current > 0) {
      selfInitiatedColumnsUpdate.current--;
      return;
    }
    columnsRef.current = formData.columns;
  }, [formData.columns]);

  // decomposedRows is initialized from formData.columns via the lazy useState
  // initializer above (computeInitialDecomposedRows) -- no mount effect needed.

  // Load available columns when component mounts or rules/joins change
  useEffect(() => {
    // AbortController to cancel in-flight requests when dependencies change
    const abortController = new AbortController();
    let isCancelled = false;

    const loadColumns = async () => {
      // Only fetch if we have rules
      if (!formData.rules || formData.rules.length === 0) {
        setAvailableColumns([]);
        setSourceInfo(null);
        setColumnLoadError(null);
        return;
      }

      try {
        setColumnLoadError(null);
        const result = await previewColumns.mutateAsync({
          rules: formData.rules,
          rule_logic: formData.rule_logic,
          joins: formData.joins && formData.joins.length > 0 ? formData.joins : undefined,
        });

        // Check if the request was cancelled before updating state
        if (!isCancelled) {
          setAvailableColumns(result.columns);
          setSourceInfo(result.source_info);
        }
      } catch (error: any) {
        // Only update error state if not cancelled
        if (!isCancelled) {
          console.error('Failed to preview columns:', error);
          const sanitizedError = sanitizeErrorMessage(error?.message || 'Failed to load available columns');
          setColumnLoadError(sanitizedError);
          setAvailableColumns([]);
          setSourceInfo(null);
        }
      }
    };

    loadColumns();

    // Cleanup function to cancel in-flight requests
    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [formData.rules, formData.joins, formData.rule_logic]);

  /**
   * Detect column name conflicts across sources
   */
  const detectColumnConflicts = useMemo(() => {
    const conflicts = new Map<string, string[]>();

    if (!sourceInfo) return conflicts;

    // Check each column name against all sources
    const allSources = {
      'Base Dataset': sourceInfo.base_columns,
      ...(sourceInfo.joined_columns || {}),
    };

    // Build map of column names to sources
    const columnToSources = new Map<string, string[]>();
    for (const [sourceName, columns] of Object.entries(allSources)) {
      for (const columnName of columns) {
        if (!columnToSources.has(columnName)) {
          columnToSources.set(columnName, []);
        }
        columnToSources.get(columnName)!.push(sourceName);
      }
    }

    // Identify conflicts (columns appearing in multiple sources)
    for (const [columnName, sources] of columnToSources.entries()) {
      if (sources.length > 1) {
        conflicts.set(columnName, sources);
      }
    }

    return conflicts;
  }, [sourceInfo]);

  /**
   * Get column source information with conflict detection
   */
  const getColumnSource = useCallback((columnName: string): {
    source: string;
    sourceType: 'base' | 'join';
    hasConflict: boolean;
    conflictSources?: string[];
  } => {
    const conflict = detectColumnConflicts.get(columnName);
    const hasConflict = !!conflict;

    if (sourceInfo?.base_columns.includes(columnName)) {
      return {
        source: 'Base Dataset',
        sourceType: 'base',
        hasConflict,
        conflictSources: conflict,
      };
    }

    if (sourceInfo?.joined_columns) {
      for (const [viewName, columns] of Object.entries(sourceInfo.joined_columns)) {
        if (columns.includes(columnName)) {
          return {
            source: viewName,
            sourceType: 'join',
            hasConflict,
            conflictSources: conflict,
          };
        }
      }
    }

    return {
      source: 'Unknown',
      sourceType: 'base',
      hasConflict,
      conflictSources: conflict,
    };
  }, [detectColumnConflicts, sourceInfo]);

  /**
   * Build ViewColumn entries for all rows (including per-source entries for conflicts).
   * Used by auto-select and handleSelectAllToggle.
   */
  const buildAllColumnEntries = useCallback((): ViewColumn[] => {
    const entries: ViewColumn[] = [];
    let order = 0;

    for (const columnName of availableColumns) {
      const conflict = detectColumnConflicts.get(columnName);
      if (conflict) {
        // One entry per source for conflicting columns
        for (const sourceName of conflict) {
          entries.push({
            id: generateUUID(),
            source_column: columnName,
            source_name: sourceName,
            column_order: order,
          });
          order++;
        }
      } else {
        const colSource = getColumnSource(columnName);
        entries.push({
          id: generateUUID(),
          source_column: columnName,
          column_order: order,
          source_name: colSource.sourceType === 'join' ? colSource.source : undefined,
        });
        order++;
      }
    }
    return entries;
  }, [availableColumns, detectColumnConflicts, getColumnSource]);

  // Pre-select all rows on initial load (once only). The hasAutoSelected ref guard
  // ensures the body executes at most once, so re-running the effect when
  // formData.columns / formData.select_all_columns change is a harmless no-op --
  // this lets us list the full dependency set without a lint suppression.
  useEffect(() => {
    if (hasAutoSelected.current) return;
    if (availableColumns.length > 0 && formData.columns.length === 0 && !formData.select_all_columns) {
      // Auto-select all columns initially
      hasAutoSelected.current = true;
      const allColumns = buildAllColumnEntries();
      columnsRef.current = allColumns;
      stableUpdateFormData({ columns: allColumns });
    }
  }, [
    availableColumns,
    detectColumnConflicts,
    buildAllColumnEntries,
    stableUpdateFormData,
    formData.columns,
    formData.select_all_columns,
  ]);

  /**
   * Toggle select all columns
   */
  const handleSelectAllToggle = (checked: boolean) => {
    if (checked) {
      // When switching to "All Columns", clear the column list and decomposed rows
      columnsRef.current = [];
      setDecomposedRows([]);
      setRowSelection({});
      // Reset initial selection sync flag since the table is about to unmount
      hasInitialSelectionSynced.current = false;
      stableUpdateFormData({
        select_all_columns: true,
        columns: []
      });
    } else {
      // When switching FROM "All Columns", select all available columns by default
      const allColumns = buildAllColumnEntries();
      columnsRef.current = allColumns;
      setDecomposedRows([]);
      stableUpdateFormData({
        select_all_columns: false,
        columns: allColumns
      });
    }
  };

  /**
   * Parse a rowId into its column name and optional source name.
   * Non-conflict rowIds are just the column name.
   * Conflict rowIds use the format "columnName::sourceName".
   */
  const parseRowId = useCallback((rowId: string): { columnName: string; sourceName?: string } => {
    const separatorIndex = rowId.indexOf('::');
    if (separatorIndex === -1) {
      return { columnName: rowId };
    }
    return {
      columnName: rowId.substring(0, separatorIndex),
      sourceName: rowId.substring(separatorIndex + 2),
    };
  }, []);

  /**
   * Check whether a rowId corresponds to a decomposed (JSON-extracted) column.
   * Decomposed rows use the ViewColumn's `id` (a UUID) as their rowId.
   */
  const isDecomposedRowId = useCallback((rowId: string): boolean => {
    return decomposedRows.some(entry => entry.id === rowId);
  }, [decomposedRows]);

  /**
   * Match a ViewColumn entry by rowId.
   * For decomposed rows, matches on the ViewColumn id.
   * For conflict rows (rowId contains "::"), matches on both source_column and source_name.
   * For non-conflict rows, matches on source_column alone.
   */
  const matchesRowId = useCallback((col: ViewColumn, rowId: string): boolean => {
    // Decomposed rows use the ViewColumn id as the rowId
    if (isDecomposedRowId(rowId)) {
      return col.id === rowId;
    }
    const { columnName, sourceName } = parseRowId(rowId);
    if (sourceName) {
      return col.source_column === columnName && col.source_name === sourceName;
    }
    // For non-decomposed, non-conflict rows: match on source_column but exclude
    // decomposed entries (which have their own dedicated rows).
    if (col.source_column === columnName) {
      // If this ViewColumn is tracked as a decomposed entry, it belongs to its
      // own decomposed row, not to the base source-column row.
      const isColDecomposed = decomposedRows.some(entry => entry.id === col.id);
      return !isColDecomposed;
    }
    return false;
  }, [parseRowId, isDecomposedRowId, decomposedRows]);

  /**
   * Eagerly update columnsRef when an alias changes, so that handleSelectionChanged
   * always sees the latest alias even before the debounced formData update fires.
   * This prevents the race condition where: type alias -> click checkbox -> debounce
   * hasn't fired yet -> handleSelectionChanged reads stale columnsRef -> alias lost.
   */
  const updateColumnsRefAlias = useCallback((rowId: string, alias: string) => {
    // Eagerly update the ref only -- do NOT increment selfInitiatedColumnsUpdate
    // here. The counter is incremented by stableUpdateFormData when the debounced
    // formData update fires, which is sufficient. Incrementing here too causes a
    // counter leak (N keystrokes + debounce = N+2 increments vs 1 decrement),
    // eventually disabling the sync useEffect and losing aliases on column toggle.
    columnsRef.current = columnsRef.current.map((col) =>
      matchesRowId(col, rowId)
        ? { ...col, column_alias: alias || undefined }
        : col
    );
  }, [matchesRowId]);

  /**
   * Handle alias change (immediate flush).
   * Reads the working column list (columnsRef) -- which is kept eagerly current by
   * updateColumnsRefAlias -- so cross-cell in-progress alias edits are preserved.
   */
  const handleAliasChangeImmediate = useCallback((rowId: string, alias: string) => {
    // Eagerly update the ref so other callbacks (e.g., handleRowSelectionChange)
    // see the latest alias immediately, not after the React render cycle.
    updateColumnsRefAlias(rowId, alias);
    stableUpdateFormData({
      columns: [...columnsRef.current], // new array reference from the already-updated ref
    });
  }, [stableUpdateFormData, updateColumnsRefAlias]);

  // Keep a ref to the latest immediate handler so the (stable) debounced dispatcher
  // below can call it without being recreated -- and without the debounce factory
  // reading/capturing a render-phase ref value.
  const handleAliasChangeImmediateRef = useRef(handleAliasChangeImmediate);
  useEffect(() => {
    handleAliasChangeImmediateRef.current = handleAliasChangeImmediate;
  }, [handleAliasChangeImmediate]);

  // Debounced propagation to formData. Implemented with a timer ref (instead of the
  // `debounce()` helper wrapped in useMemo) so that no ref is read or passed to an
  // opaque function during render -- the refs are only touched inside the timer/
  // event callbacks. Stable identity ([] deps) keeps columnDefs stable.
  const aliasDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleAliasChange = useMemo(() => {
    const debounced = ((rowId: string, alias: string) => {
      if (aliasDebounceTimerRef.current) clearTimeout(aliasDebounceTimerRef.current);
      aliasDebounceTimerRef.current = setTimeout(() => {
        aliasDebounceTimerRef.current = null;
        handleAliasChangeImmediateRef.current(rowId, alias);
      }, UI_CONSTANTS.ALIAS_DEBOUNCE_DELAY);
    }) as ((rowId: string, alias: string) => void) & { cancel: () => void };
    debounced.cancel = () => {
      if (aliasDebounceTimerRef.current) {
        clearTimeout(aliasDebounceTimerRef.current);
        aliasDebounceTimerRef.current = null;
      }
    };
    return debounced;
  }, []);

  /**
   * Handle transformation edit.
   * Now receives a rowId which encodes both column name and source.
   * Wrapped in useCallback to keep columnDefs stable across renders.
   */
  const handleTransformationEdit = useCallback((rowId: string) => {
    setEditingColumnName(rowId);
    setTransformationDialogOpen(true);
  }, []);

  /**
   * Open batch JSON decomposition dialog for a column.
   */
  const handleBatchDecomposeEdit = useCallback((rowId: string) => {
    setBatchDecomposeColumn(rowId);
    setBatchDecomposeOpen(true);
  }, []);

  /**
   * Handle batch JSON decomposition apply.
   * Replaces all existing entries for the source column with the new decomposed entries.
   * Each decomposed entry gets its own row in the table so that individual fields
   * are visible, selectable, and configurable.
   */
  const handleBatchDecomposeApply = useCallback((
    newColumns: Array<{
      source_column: string;
      column_alias: string;
      transform_expression: string;
      column_order: number;
    }>
  ) => {
    if (newColumns.length === 0) return;
    const sourceCol = newColumns[0].source_column;

    // Determine the rowId for matching — either from pre-selected column or from the source_column
    const rowIdToMatch = batchDecomposeColumn || sourceCol;

    // Remove all existing entries for this source column (including previous decomposed entries)
    const prevDecomposedIds = new Set(
      decomposedRows.filter(e => e.sourceColumn === sourceCol).map(e => e.id)
    );
    const otherColumns = columnsRef.current.filter(col => {
      // Remove entries matching the base row
      if (matchesRowId(col, rowIdToMatch)) return false;
      // Remove entries from previous decomposition of this source column
      if (prevDecomposedIds.has(col.id)) return false;
      return true;
    });

    // Resolve source_name from the rowId if it was opened from a conflict column
    const sourceName = batchDecomposeColumn
      ? parseRowId(batchDecomposeColumn).sourceName || undefined
      : undefined;

    // Create new ViewColumn entries for each decomposed field
    const decomposed: ViewColumn[] = newColumns.map((col, idx) => ({
      id: generateUUID(),
      source_column: col.source_column,
      column_alias: col.column_alias,
      transform_expression: col.transform_expression,
      column_order: otherColumns.length + idx,
      source_name: sourceName,
    }));

    // Update decomposedRows state: remove old entries for this source column, add new ones
    setDecomposedRows(prev => {
      const filtered = prev.filter(e => e.sourceColumn !== sourceCol);
      const newEntries: DecomposedRowEntry[] = decomposed.map(col => ({
        id: col.id,
        sourceColumn: col.source_column,
        alias: col.column_alias || '',
        sourceName: col.source_name,
        viewColumn: col,
      }));
      return [...filtered, ...newEntries];
    });

    const merged = [...otherColumns, ...decomposed];
    columnsRef.current = merged;
    selfInitiatedColumnsUpdate.current++;
    stableUpdateFormData({ columns: merged });
    setBatchDecomposeColumn(null);
  }, [batchDecomposeColumn, matchesRowId, parseRowId, stableUpdateFormData, decomposedRows]);

  /**
   * Handle transformation save
   * Uses ref to always get the latest column state.
   * editingColumnName is now a rowId.
   */
  const handleTransformationChange = useCallback((transform_expression: string | undefined) => {
    if (editingColumnName) {
      // Update the ref only — do NOT call stableUpdateFormData here.
      // Committing to formData on every onSqlGenerate call causes an infinite
      // re-render loop because the builder's useEffect fires on mount/change,
      // which triggers formData update, which triggers re-render, which
      // re-fires the useEffect. formData is committed when the dialog closes.
      const updatedColumns = columnsRef.current.map((col) =>
        matchesRowId(col, editingColumnName)
          ? { ...col, transform_expression }
          : col
      );
      columnsRef.current = updatedColumns;
    }
  }, [editingColumnName, matchesRowId]);

  /**
   * Prepare the structural row data with conflict detection.
   * Conflicting columns (same name in multiple sources) get one row per source.
   * Non-conflicting columns get a single row.
   *
   * This memo depends only on the grid STRUCTURE (available columns, source info,
   * decomposed rows, filter) -- NOT on formData.columns -- so it is stable across
   * alias/transform edits. Per-row committed display state is attached separately
   * by the `displayRows` memo below.
   */
  const rowData = useMemo<ColumnRowData[]>(() => {
    const rows: ColumnRowData[] = [];

    if (!sourceInfo) {
      // No source info yet, fall back to flat list with no conflict info
      for (const columnName of availableColumns) {
        rows.push({
          rowId: columnName,
          columnName,
          source: 'Base Dataset',
          sourceType: 'base',
          hasConflict: false,
        });
      }
    } else {
      // Build all sources map
      const allSources: Record<string, { columns: string[]; sourceType: 'base' | 'join' }> = {
        'Base Dataset': { columns: sourceInfo.base_columns, sourceType: 'base' },
      };
      if (sourceInfo.joined_columns) {
        for (const [name, cols] of Object.entries(sourceInfo.joined_columns)) {
          allSources[name] = { columns: cols, sourceType: 'join' };
        }
      }

      // Build a set of source columns that have active decomposed entries
      const decomposedSourceColumns = new Set(decomposedRows.map(e => e.sourceColumn));

      // Track which columns we've already emitted (for non-conflicting columns)
      const emitted = new Set<string>();

      // Process each unique column name from the deduplicated list
      for (const columnName of availableColumns) {
        const conflict = detectColumnConflicts.get(columnName);
        const hasConflict = !!conflict;

        if (hasConflict && conflict) {
          // Emit one row per source that has this column
          for (const sourceName of conflict) {
            // Skip this source row if it has been decomposed
            const isThisSourceDecomposed = decomposedRows.some(
              e => e.sourceColumn === columnName && e.sourceName === sourceName
            );
            if (isThisSourceDecomposed) continue;

            const sourceData = allSources[sourceName];
            const rowId = `${columnName}::${sourceName}`;
            rows.push({
              rowId,
              columnName,
              source: sourceName,
              sourceType: sourceData?.sourceType || 'base',
              hasConflict: true,
              conflictSources: conflict,
            });
          }
        } else {
          // Skip non-conflict columns that have been fully decomposed
          if (decomposedSourceColumns.has(columnName)) continue;

          // Non-conflicting: emit a single row
          if (!emitted.has(columnName)) {
            const { source, sourceType } = getColumnSource(columnName);
            // Use qualified rowId for join columns to match selectionKey format
            const rowId = sourceType === 'join' ? `${columnName}::${source}` : columnName;
            rows.push({
              rowId,
              columnName,
              source,
              sourceType,
              hasConflict: false,
            });
            emitted.add(columnName);
          }
        }
      }
    }

    // Append rows for JSON-decomposed columns.
    // Each decomposed entry gets its own row so it is visible and selectable individually.
    for (const entry of decomposedRows) {
      rows.push({
        rowId: entry.id,
        columnName: entry.alias || entry.sourceColumn,
        source: entry.sourceName || 'Base Dataset',
        sourceType: 'decomposed',
        hasConflict: false,
        isDecomposed: true,
        decomposedFromColumn: entry.sourceColumn,
      });
    }

    // Apply source filter
    if (sourceFilter !== 'all') {
      return rows.filter(row => {
        if (row.isDecomposed) {
          // Decomposed rows match the source filter of their parent column
          return row.source === sourceFilter;
        }
        return row.source === sourceFilter;
      });
    }

    return rows;
  }, [availableColumns, sourceInfo, sourceFilter, detectColumnConflicts, decomposedRows]);

  /**
   * Attach committed display state (alias, aliasRequired, transform info) to each
   * structural row, derived from formData.columns (the single source of truth).
   * This is what the DataTable renders and what the cell renderers read from
   * their `data` prop -- so cells never read a ref during render.
   */
  const displayRows = useMemo<ColumnRowData[]>(() => {
    return rowData.map((row) => {
      const matching = formData.columns.filter((col) => matchesRowId(col, row.rowId));
      const withTransform = matching.filter((c) => c.transform_expression);

      // Conflict cells require an alias once 2+ of the conflict's source versions
      // are selected (present in formData.columns).
      let aliasRequired = false;
      if (row.hasConflict && row.conflictSources) {
        const selectedCount = row.conflictSources.filter((src) =>
          formData.columns.some((col) => matchesRowId(col, `${row.columnName}::${src}`))
        ).length;
        aliasRequired = selectedCount > 1;
      }

      return {
        ...row,
        alias: matching[0]?.column_alias || '',
        aliasRequired,
        transformExpression: withTransform[0]?.transform_expression,
        transformFieldCount: withTransform.length,
      };
    });
  }, [rowData, formData.columns, matchesRowId]);

  /**
   * Handle row-selection change from DataTable (user clicked a checkbox).
   * Maps TanStack rowSelection state → formData.columns, preserving aliases
   * and transformations on retained rows.
   *
   * IMPORTANT: This callback runs ON EVERY render of DataTable that bumps
   * rowSelection — including the renders our sync effect causes. The guards
   * isSyncingSelection / hasInitialSelectionSynced gate those out so we
   * don't echo programmatic changes back through stableUpdateFormData.
   */
  const handleRowSelectionChange = useCallback((next: RowSelectionState) => {
    // Always reflect the new selection state, even when guarded — DataTable
    // needs the prop to match what its internal state will become.
    setRowSelection(next);

    if (isSyncingSelection.current) return;
    if (!hasInitialSelectionSynced.current) return;

    const selectedRows: ColumnRowData[] = rowData.filter((r) => next[r.rowId]);

    const currentDecomposedIds = new Set(decomposedRows.map(e => e.id));
    const currentRowIds = new Set(columnsRef.current.map(col => {
      if (currentDecomposedIds.has(col.id)) return col.id;
      const needsQualification = col.source_name && (
        joinedColumnKeys.has(col.source_name) || detectColumnConflicts.has(col.source_column)
      );
      return needsQualification ? `${col.source_column}::${col.source_name}` : col.source_column;
    }));
    const newRowIds = new Set(selectedRows.map(row => row.rowId));
    if (currentRowIds.size === newRowIds.size && [...currentRowIds].every(id => newRowIds.has(id))) {
      return;
    }

    // Build selected columns from the now-selected rows
    const selectedColumns: ViewColumn[] = [];
    selectedRows.forEach((row) => {
      const existingConfigs = columnsRef.current.filter(col => matchesRowId(col, row.rowId));
      if (existingConfigs.length > 0) {
        existingConfigs.forEach((config) => {
          selectedColumns.push({
            ...config,
            column_order: selectedColumns.length,
          });
        });
      } else if (row.isDecomposed && row.decomposedFromColumn) {
        const decomposedEntry = decomposedRows.find(e => e.id === row.rowId);
        if (decomposedEntry?.viewColumn) {
          selectedColumns.push({
            ...decomposedEntry.viewColumn,
            column_order: selectedColumns.length,
          });
        } else {
          // Fallback: construct minimal entry (transform_expression will be absent)
          selectedColumns.push({
            id: row.rowId,
            source_column: row.decomposedFromColumn,
            column_alias: row.columnName,
            column_order: selectedColumns.length,
          });
        }
      } else {
        selectedColumns.push({
          id: generateUUID(),
          source_column: row.columnName,
          column_order: selectedColumns.length,
          source_name: row.sourceType === 'join' || row.hasConflict ? row.source : undefined,
        });
      }
    });

    // Eagerly update the ref so it stays authoritative
    columnsRef.current = selectedColumns;
    stableUpdateFormData({ columns: selectedColumns });
    // rowData captured below — make sure deps list it
  }, [stableUpdateFormData, matchesRowId, decomposedRows, joinedColumnKeys, detectColumnConflicts]);

  /**
   * Select all / Deselect all (using current rowData snapshot).
   * Routes through handleRowSelectionChange so formData.columns updates.
   */
  const handleSelectDeselectAll = () => {
    const allSelected = rowData.length > 0 && rowData.every((r) => rowSelection[r.rowId]);
    if (allSelected) {
      handleRowSelectionChange({});
    } else {
      const next: RowSelectionState = {};
      for (const r of rowData) next[r.rowId] = true;
      handleRowSelectionChange(next);
    }
  };

  /**
   * Get list of available sources for filtering
   */
  const availableSources = useMemo(() => {
    const sources = new Set<string>(['Base Dataset']);
    if (sourceInfo?.joined_columns) {
      Object.keys(sourceInfo.joined_columns).forEach(source => sources.add(source));
    }
    return Array.from(sources);
  }, [sourceInfo]);

  /**
   * TanStack column definitions. Includes a leading "select" column with a
   * row checkbox + header (select-all) checkbox.
   *
   * Cell renderers read committed values from their `data` row prop (built by the
   * `displayRows` memo from formData.columns) -- never from a ref during render.
   */
  const columns = useMemo<ColumnDef<ColumnRowData>[]>(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllRowsSelected()
              ? true
              : table.getIsSomeRowsSelected()
                ? 'indeterminate'
                : false
          }
          onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
          aria-label="Select all rows"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={`Select row ${row.original.rowId}`}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      size: 40,
    },
    {
      id: 'columnName',
      accessorKey: 'columnName',
      header: 'Column Name',
      cell: ({ row }) => <ColumnNameRenderer data={row.original} />,
    },
    {
      id: 'source',
      accessorKey: 'source',
      header: 'Source',
      cell: ({ row }) => <SourceBadgeRenderer data={row.original} />,
    },
    {
      id: 'alias',
      header: 'Alias',
      cell: ({ row }) => (
        <AliasCellRenderer
          data={row.original}
          onAliasChange={handleAliasChange}
          onAliasChangeImmediate={handleAliasChangeImmediate}
          onAliasChangeRef={updateColumnsRefAlias}
        />
      ),
    },
    {
      id: 'transformation',
      header: 'Transformation',
      cell: ({ row }) => (
        <TransformationCellRenderer
          data={row.original}
          onEdit={handleTransformationEdit}
          onBatchEdit={handleBatchDecomposeEdit}
        />
      ),
    },
  ], [
    handleAliasChange,
    handleAliasChangeImmediate,
    updateColumnsRefAlias,
    handleTransformationEdit,
    handleBatchDecomposeEdit,
  ]);

  const getRowId = useCallback((row: ColumnRowData) => row.rowId, []);

  /**
   * Detect active column conflicts where multiple source versions are selected.
   * Used to render the conflict warning alert in the JSX.
   */
  const activeConflicts = useMemo(() => {
    const conflicts = new Map<string, string[]>();
    for (const [columnName, sources] of detectColumnConflicts.entries()) {
      const selectedSources = sources.filter(src =>
        formData.columns.some(col => col.source_column === columnName && col.source_name === src)
      );
      if (selectedSources.length > 1) {
        conflicts.set(columnName, selectedSources);
      }
    }
    return conflicts;
  }, [detectColumnConflicts, formData.columns]);

  /**
   * Stable selection key: a sorted, joined string of selected rowIds.
   * This only changes when columns are added or removed, NOT when aliases
   * or transformations are edited. This prevents the selection sync effect
   * from firing on every alias keystroke.
   *
   * For decomposed columns, the ViewColumn's `id` is used as the rowId
   * (matching the decomposedRows entries) instead of source_column.
   */
  const decomposedIdSet = useMemo(() => new Set(decomposedRows.map(e => e.id)), [decomposedRows]);
  const selectionKey = useMemo(() => {
    return formData.columns
      .map(col => {
        // Decomposed entries use their ViewColumn id as the rowId
        if (decomposedIdSet.has(col.id)) {
          return col.id;
        }
        // Qualify with source_name when:
        // 1. It matches a known joined source (non-conflict join columns), OR
        // 2. The column name is a conflict (same name in multiple sources).
        //    Conflict rows ALWAYS use qualified rowIds ("name::Base Dataset"),
        //    even for the base dataset source.
        // In view-on-view scenarios, the backend stores the base view name as
        // source_name on base columns, but the grid treats those as unqualified
        // base rows. Qualifying them here would cause a rowId mismatch — unless
        // the column is also a conflict column.
        const needsQualification = col.source_name && (
          joinedColumnKeys.has(col.source_name) || detectColumnConflicts.has(col.source_column)
        );
        return needsQualification ? `${col.source_column}::${col.source_name}` : col.source_column;
      })
      .sort()
      .join('\n');
  }, [formData.columns, decomposedIdSet, joinedColumnKeys, detectColumnConflicts]);

  /**
   * Stable key representing the set of rowIds currently in the grid.
   * Changes when sourceInfo loads and conflict rows replace simple rows
   * (e.g. "name" becomes "name::Base Dataset" + "name::dealhub_versions").
   * Used as a dependency for the selection sync effect so it re-fires
   * when the grid row structure changes.
   */
  const rowStructureKey = useMemo(() => {
    return rowData.map(r => r.rowId).sort().join('\n');
  }, [rowData]);

  /**
   * Sync rowSelection state from formData.columns when selectionKey changes.
   * Only reacts to actual selection changes (columns added/removed),
   * NOT to alias or transformation edits on already-selected columns.
   *
   * Also re-fires when rowStructureKey changes. This handles the case where
   * sourceInfo loads after availableColumns: the row IDs change from simple
   * names (e.g. "name") to conflict-qualified names (e.g. "name::Base
   * Dataset", "name::dealhub_versions"), and the selection must be re-synced
   * to match the new IDs.
   */
  useEffect(() => {
    if (availableColumns.length === 0) return;

    // Build a set of keys that match the rowId format
    const selectedKeys = new Set<string>(selectionKey.split('\n').filter(Boolean));

    const next: RowSelectionState = {};
    for (const r of rowData) {
      if (selectedKeys.has(r.rowId)) next[r.rowId] = true;
    }

    // Suppress handleRowSelectionChange echo while we programmatically sync.
    // The setRowSelection + flag reset are applied together in a microtask (rather
    // than synchronously in the effect body) so that any synchronously-batched
    // state-update callbacks still see isSyncingSelection=true, and so the state
    // update is not a synchronous effect-body setState.
    isSyncingSelection.current = true;
    queueMicrotask(() => {
      setRowSelection(next);
      isSyncingSelection.current = false;
      hasInitialSelectionSynced.current = true;
    });
  }, [selectionKey, availableColumns, rowStructureKey, rowData]);

  // Count selected columns.
  // totalCount reflects the actual number of grid rows (including per-source conflict rows).
  const selectedCount = formData.columns.length;
  const totalCount = rowData.length;

  return (
    <div className="space-y-6">
      {/* Select All Toggle */}
      <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
        <div className="flex items-center gap-3">
          <Switch
            id="select-all-columns"
            checked={formData.select_all_columns}
            onCheckedChange={handleSelectAllToggle}
          />
          <Label htmlFor="select-all-columns" className="cursor-pointer">
            Select All Columns (*)
          </Label>
        </div>
        <p className="text-sm text-muted-foreground">
          Includes all columns from matched files
        </p>
      </div>

      {/* Custom Column Configuration */}
      {!formData.select_all_columns && (
        <div className="space-y-4">
          {/* Loading State */}
          {previewColumns.isPending && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading available columns...</span>
            </div>
          )}

          {/* Error State */}
          {columnLoadError && (
            <Alert variant="destructive">
              <AlertDescription>
                {columnLoadError}
              </AlertDescription>
            </Alert>
          )}

          {/* Column Selection Grid */}
          {!previewColumns.isPending && !columnLoadError && availableColumns.length > 0 && (
            <>
              {/* Conflict Warning - only show if both versions of any conflicting column are selected */}
              {activeConflicts.size > 0 && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="space-y-2">
                      <div>
                        {activeConflicts.size} column name{activeConflicts.size > 1 ? 's have' : ' has'} multiple source versions selected.
                        Set an alias on at least one version to avoid ambiguity, or deselect the version you don't need.
                      </div>
                      <div className="text-xs space-y-1">
                        {Array.from(activeConflicts.entries()).map(([columnName, sources]) => (
                          <div key={columnName} className="font-mono">
                            <span className="font-semibold">{columnName}</span>: {sources.join(', ')}
                          </div>
                        ))}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Toolbar with Search, Filter, and Actions */}
              <div className="flex gap-2 items-center flex-wrap">
                <div className="flex-1 relative min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Quick search columns..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setQuickFilterText(e.target.value);
                    }}
                    className="pl-10"
                  />
                </div>

                {/* Source Filter */}
                {availableSources.length > 1 && (
                  <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Filter by source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      {availableSources.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source === 'Base Dataset' ? (
                            <span className="flex items-center gap-1">
                              <Database className="h-3 w-3" />
                              {source}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <Link2 className="h-3 w-3" />
                              {source}
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Badge variant="secondary" className="px-3 py-1.5">
                  {selectedCount} / {totalCount} selected
                </Badge>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSelectDeselectAll}
                >
                  {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setBatchDecomposeColumn(null);
                    setBatchDecomposeOpen(true);
                  }}
                  className="gap-1"
                >
                  <Layers className="h-4 w-4" />
                  Decompose JSON
                </Button>
              </div>

              {/* Column DataTable */}
              <ErrorBoundary
                resetKeys={[rowData.length, columns.length]}
                onError={(error, errorInfo) => {
                  console.error('Column DataTable Error Boundary caught error:', error);
                  console.error('Component stack:', errorInfo.componentStack);
                }}
              >
                <Card>
                  <CardContent className="pt-6">
                    <div style={{ minHeight: UI_CONSTANTS.GRID_HEIGHT }}>
                      <DataTable
                        data={displayRows}
                        columns={columns}
                        getRowId={getRowId}
                        globalFilter={quickFilterText}
                        rowSelection={rowSelection}
                        onRowSelectionChange={handleRowSelectionChange}
                        hidePagination
                      />
                    </div>
                  </CardContent>
                </Card>
              </ErrorBoundary>
            </>
          )}

          {/* No columns available */}
          {!previewColumns.isPending && !columnLoadError && availableColumns.length === 0 && (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <p className="text-muted-foreground">
                No columns available. Make sure you've configured file selection rules in the previous step.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Transformation Dialog */}
      <Dialog open={transformationDialogOpen} onOpenChange={setTransformationDialogOpen}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] overflow-y-auto"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Column Transformation</DialogTitle>
            <DialogDescription>
              Configure SQL transformation for column: <code className="font-mono text-sm">{editingColumnName ? parseRowId(editingColumnName).columnName : ''}</code>
              {editingColumnName && parseRowId(editingColumnName).sourceName && (
                <span className="text-muted-foreground"> (from {parseRowId(editingColumnName).sourceName})</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {editingColumnName && (
            <div className="py-4">
              <ColumnTransformationBuilder
                columnName={parseRowId(editingColumnName).columnName}
                currentTransform={
                  formData.columns.find(col => matchesRowId(col, editingColumnName))?.transform_expression
                }
                onTransformChange={handleTransformationChange}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                // Commit pending transformation changes to formData
                stableUpdateFormData({ columns: columnsRef.current });
                setTransformationDialogOpen(false);
                setEditingColumnName(null);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch JSON Decompose Dialog */}
      {batchDecomposeOpen && (
        <BatchJsonDecompose
          open={batchDecomposeOpen}
          onOpenChange={setBatchDecomposeOpen}
          sourceColumn={
            batchDecomposeColumn
              ? parseRowId(batchDecomposeColumn).columnName
              : ''
          }
          availableColumns={availableColumns}
          existingExtractions={
            batchDecomposeColumn
              ? formData.columns
                  .filter(col => matchesRowId(col, batchDecomposeColumn) && col.transform_expression)
                  .map(col => {
                    // Extract the key from the transform_expression (e.g., "col"->>'Category' → Category)
                    const keyMatch = col.transform_expression?.match(/->>'([^']+)'/);
                    // Extract return type from TRY_CAST wrapper if present
                    const typeMatch = col.transform_expression?.match(/TRY_CAST\(.*\s+AS\s+(\w+)\)/i);
                    return {
                      key: keyMatch?.[1] || col.column_alias || '',
                      alias: col.column_alias || '',
                      returnType: typeMatch?.[1] || 'VARCHAR',
                    };
                  })
              : undefined
          }
          onApply={handleBatchDecomposeApply}
        />
      )}

      {/* Help Text */}
      <div className="mt-6 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-2">Column Configuration Tips</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
          <li>Use "Select All Columns" for simplicity or uncheck to select specific columns</li>
          <li>All columns are pre-selected by default - deselect the ones you don't need</li>
          <li>Use the quick search to filter columns in views with many columns</li>
          <li>Filter by source to show only base dataset or specific joined view columns</li>
          <li>Columns with <Badge variant="destructive" className="inline-flex h-4 text-xs">Conflict</Badge> badges appear in multiple sources - deselect one version or set aliases to avoid ambiguity</li>
          <li>Click on the Alias field to rename columns in the view output</li>
          <li>Click "Add" or "Edit" in the Transformation column to apply SQL transformations</li>
          <li>Click "Decompose JSON" to extract multiple fields from a JSON column into separate view columns</li>
          <li>Base dataset columns are marked with <Database className="inline h-3 w-3" /> icon</li>
          <li>Joined view columns are marked with <Link2 className="inline h-3 w-3" /> icon</li>
        </ul>
      </div>
    </div>
  );
};
